import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import type { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import type { RawTransfer, TransferRow, TransferDialogResult, TransfersFilter } from '@/types/monitoring';
import { useTransfersQuery } from './api';
import { TransferDetailsModal } from './TransferDetailsModal';
import { getProviderIconSrc } from './providerIcon';

const AUTO_REFRESH_MS = 30_000;
const DEFAULT_FILTER: TransfersFilter = { nbTransfers: 20, startDate: null, endDate: null };

function mapTransfer(t: RawTransfer): TransferRow {
  const operation = t.transferType.toLowerCase();
  const base: TransferRow = {
    date: t.date,
    operation,
    shop: t.shopName,
    amount: t.amount,
    internalReference: t.id,
    status: t.status,
    sender: null,
    provider: null,
    receiver: null,
    opsAccountNumber: null,
    externalReference: null,
    serviceType: null,
  };

  switch (operation) {
    case 'cashout':
      return {
        ...base,
        sender: t.senderProviderAccountId ?? null,
        provider: t.senderProviderIdentifier ?? null,
        receiver: t.recipientUserAlias ?? null,
        opsAccountNumber: t.senderOperatorAccountNumber ?? null,
        externalReference: t.extSenderTransactionRef ?? null,
        serviceType: t.senderServiceType ?? null,
      };
    case 'internal_transfer':
      return {
        ...base,
        sender: t.senderUserAlias ?? null,
        provider: t.senderProviderIdentifier ?? null,
        receiver: t.recipientUserAlias ?? null,
        opsAccountNumber: '',
        externalReference: '',
        serviceType: t.senderServiceType ?? null,
      };
    default:
      return {
        ...base,
        sender: t.senderUserAlias ?? null,
        provider: t.recipientProviderIdentifier ?? null,
        receiver: t.recipientProviderAccountId ?? null,
        opsAccountNumber: t.recipientOperatorAccountNumber ?? null,
        externalReference: t.extRecipientTransactionRef ?? null,
        serviceType: t.recipientServiceType ?? null,
      };
  }
}

function rowBg(status: number): string {
  if (status === 0) return 'success.light';
  if (status === 50) return 'warning.light';
  return 'error.light';
}

export function TransfersMonitoringPage() {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();

  // Form state
  const [nbTransfers, setNbTransfers] = useState(20);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Committed filter drives the query
  const [committedFilter, setCommittedFilter] = useState<TransfersFilter>(DEFAULT_FILTER);

  const isFilterValid =
    (startDate === null && endDate === null) ||
    (startDate !== null && endDate !== null && !startDate.isAfter(endDate));

  const query = useTransfersQuery(committedFilter, autoRefresh ? AUTO_REFRESH_MS : false);

  const allRows = useMemo(
    () => (query.data?.transfers ?? []).map(mapTransfer),
    [query.data],
  );

  // Client-side text filter
  const [textFilter, setTextFilter] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const filtered = useMemo(() => {
    if (!textFilter.trim()) return allRows;
    const term = textFilter.toLowerCase();
    return allRows.filter(
      (r) =>
        (r.internalReference && r.internalReference.toString().includes(term)) ||
        (r.sender && r.sender.toLowerCase().includes(term)) ||
        (r.receiver && r.receiver.toLowerCase().includes(term)) ||
        (r.shop && r.shop.toLowerCase().includes(term)) ||
        (r.operation && r.operation.toLowerCase().includes(term)) ||
        (r.opsAccountNumber && r.opsAccountNumber.toString().includes(term)) ||
        (r.amount && r.amount.toString().includes(term)),
    );
  }, [allRows, textFilter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleFetch = () => {
    setTextFilter('');
    setPage(0);
    setCommittedFilter({
      nbTransfers,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
    });
  };

  const handleReset = () => {
    setNbTransfers(20);
    setStartDate(null);
    setEndDate(null);
    setTextFilter('');
    setPage(0);
    setCommittedFilter({ ...DEFAULT_FILTER });
  };

  const handleRowClick = async (transfer: TransferRow) => {
    const result = await openDialog<TransferDialogResult | null>((close) => (
      <TransferDetailsModal transfer={transfer} onClose={close} />
    ));
    if (!result) return;
    if (result.code === 0) {
      const msg =
        result.type === 'cancel'
          ? t('pages.monitoring.transfers.transfer_cancelled_successfully', {
              defaultValue: 'Transfer cancelled successfully.',
            })
          : t('pages.monitoring.transfers.transfer_corrected_successfully', {
              defaultValue: 'Transfer corrected successfully.',
            });
      await Swal.fire(
        t('pages.monitoring.transfers.success', { defaultValue: 'Success' }),
        msg,
        'success',
      );
      handleReset();
    } else {
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        t('pages.monitoring.transfers.transfer_has_pending_transaction', {
          defaultValue: 'Transfer has a pending transaction.',
        }),
        'error',
      );
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.monitoring_transfers', { defaultValue: 'Transfers Monitoring' })}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          flexWrap="wrap"
        >
          <TextField
            label={t('pages.monitoring.transfers.nb_transfers', {
              defaultValue: 'Number of transfers',
            })}
            type="number"
            size="small"
            value={nbTransfers}
            onChange={(e) => setNbTransfers(Number(e.target.value))}
            sx={{ minWidth: 160 }}
          />
          <DateTimePicker
            label={t('pages.monitoring.corrections.start_date', { defaultValue: 'Start date' })}
            value={startDate}
            onChange={(val) => setStartDate(val)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DateTimePicker
            label={t('pages.monitoring.corrections.end_date', { defaultValue: 'End date' })}
            value={endDate}
            onChange={(val) => setEndDate(val)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="contained"
            disabled={!isFilterValid}
            onClick={handleFetch}
          >
            {t('pages.monitoring.transfers.fetch', { defaultValue: 'Fetch' })}
          </Button>
          <Button variant="contained" color="error" onClick={handleReset}>
            {t('pages.monitoring.transfers.reset', { defaultValue: 'Reset' })}
          </Button>
        </Stack>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label={t('pages.monitoring.transfers.auto_refresh', { defaultValue: 'Auto-refresh' })}
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <TextField
          size="small"
          value={textFilter}
          onChange={(e) => {
            setTextFilter(e.target.value);
            setPage(0);
          }}
          placeholder={t('pages.user-list.filter', { defaultValue: 'Filter…' })}
          sx={{ mb: 2, minWidth: 280 }}
        />

        {query.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.date', { defaultValue: 'Date' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.operation', {
                        defaultValue: 'Operation',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.sender', { defaultValue: 'Sender' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.receiver', {
                        defaultValue: 'Receiver',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.shop', { defaultValue: 'Shop' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.operation_account_number', {
                        defaultValue: 'Account #',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.amount', { defaultValue: 'Amount' })}
                    </TableCell>
                    <TableCell>
                      {t('pages.monitoring.transfers.table.internal_reference', {
                        defaultValue: 'Ref',
                      })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((row) => {
                    const iconSrc = getProviderIconSrc(row.provider, row.serviceType);
                    return (
                      <TableRow
                        key={row.internalReference}
                        hover
                        sx={{ bgcolor: rowBg(row.status), cursor: 'pointer' }}
                        onClick={() => void handleRowClick(row)}
                      >
                        <TableCell>
                          {iconSrc && (
                            <Box
                              component="img"
                              src={iconSrc}
                              alt="provider"
                              sx={{ width: 20, height: 20, mr: 0.5, verticalAlign: 'middle' }}
                            />
                          )}
                          {new Date(row.date).toLocaleString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell>
                          {t(`pages.monitoring.transfers.${row.operation}`, {
                            defaultValue: row.operation,
                          })}
                        </TableCell>
                        <TableCell>{row.sender}</TableCell>
                        <TableCell>{row.receiver}</TableCell>
                        <TableCell>{row.shop}</TableCell>
                        <TableCell>{row.opsAccountNumber}</TableCell>
                        <TableCell>{row.amount}</TableCell>
                        <TableCell>{row.internalReference}</TableCell>
                      </TableRow>
                    );
                  })}
                  {paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('pages.monitoring.transfers.no_transfers_found', {
                            defaultValue: 'No transfers found.',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
